import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Session from 'supertokens-auth-react/recipe/session';

import { useApiClient } from '@eluve/api-client-provider';
import { LoadingIcon, Stack, Text, useToast } from '@eluve/components';

export const ImpersonationLoginPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [searchParams] = useSearchParams();
  const didImpersonate = useRef(false);

  useEffect(() => {
    const performImpersonation = async () => {
      const targetUserId = searchParams.get('targetUserId');
      const tenantId = searchParams.get('tenantId');
      const reason = searchParams.get('reason');
      const originUrl = searchParams.get('originUrl');

      if (!targetUserId || !tenantId || !reason) {
        toast.error('Invalid impersonation parameters');
        navigate('/admin');
        return;
      }

      try {
        const result = await apiClient.auth.impersonateUser({
          body: {
            targetUserId,
            tenantId,
            reason,
            originUrl: originUrl ?? undefined,
          },
        });

        if (result.status === 200) {
          toast.success(result.body.message);
          // The impersonate endpoint returns the new SuperTokens session in
          // response headers (`st-access-token`, `front-token`), but the
          // js-readable `st-access-token` cookie is not always rewritten when
          // the call goes through ts-rest's bare fetch — leaving the next page
          // load to read whichever JWT was last written by the SDK's own
          // refresh path. That JWT still carries either the admin session or a
          // previous impersonation's `expiresAt`, so ImpersonationExpiryMonitor
          // fires "Expired" immediately on landing.
          //
          // Force a SuperTokens refresh before redirecting: the SDK owns the
          // /session/refresh request end-to-end, so it reliably swaps the
          // js-readable cookie + sFrontToken to the freshly-issued impersonated
          // session before the hard navigation reads cookies. [ENG-4309]
          await Session.attemptRefreshingSession();
          window.location.href = `/organizations/${tenantId}/home`;
        } else {
          toast.error('Failed to start impersonation');
          navigate('/admin');
        }
      } catch (error: unknown) {
        const errorMessage =
          (error as { body?: { message?: string } })?.body?.message ??
          'Failed to start impersonation';
        toast.error(errorMessage);
        navigate('/admin');
      }
    };

    // Prevent double execution in StrictMode
    if (didImpersonate.current === false) {
      didImpersonate.current = true;
      performImpersonation();
    }
  }, [navigate, apiClient, searchParams, toast]);

  return (
    <div className="grid h-screen place-items-center">
      <Stack gap={4} align="center">
        <LoadingIcon size="3xl" className="text-border-accent-quaternary" />
        <Text size="xs" color="secondary">
          Starting impersonation...
        </Text>
      </Stack>
    </div>
  );
};
